<script lang="ts">
    import { CloseButton, Drawer, Sidebar, SidebarGroup, SidebarItem, SidebarWrapper, } from 'flowbite-svelte';
    import { sineIn } from 'svelte/easing';
    import MoneyBillWave from "../icons/MoneyBillWave.svelte";
    import Shield from "../icons/Shield.svelte";
    import GraduationCap from "../icons/GraduationCap.svelte";
    import Ambulance from "../icons/Ambulance.svelte";
    import StarOfLife from "../icons/StarOfLife.svelte";
    import Feedback from "../icons/Feedback.svelte";
    import Cog from "../icons/Cog.svelte";
    import Info from "../icons/Info.svelte";
    import * as Sentry from "@sentry/svelte";
    import SettingsModal from "./SettingsModal.svelte";
    import AboutModal from "./AboutModal.svelte";
    import { twMerge } from "tailwind-merge";
    import { TempState } from "../state.svelte";
    import MedicalBook from "../icons/MedicalBook.svelte";
    import Beta from "../icons/Beta.svelte";

    const divClass = twMerge("overflow-y-auto py-4 px-3 rounded h-full black:bg-gray-950",
        "flex flex-col justify-between [&_a>span]:frappe:text-ctp-subtext1",
        "[&_a:hover]:frappe:bg-ctp-pink [&_a:hover>svg]:frappe:text-ctp-crust",
        "[&_a:hover>span]:frappe:text-ctp-base black:text-gray-200",
        "[&_a:hover>span]:black:text-black [&_a>span]:black:text-gray-200"
    );

    const feedback = Sentry.getFeedback();

    let settingsModal: boolean = $state(false);
    let aboutModal: boolean = $state(false);

    let transitionParams = {
        x: -320,
        duration: 200,
        easing: sineIn
    };

    function closeSidebar() {
        TempState.sideBarHidden = true;
    }

    async function openFeedback(e: Event) {
        e.preventDefault();
        if (!feedback)
            return;

        closeSidebar();
        const form = await feedback.createForm();
        form.appendToDom();
        form.open();
    }

    async function openSettings(e: Event) {
        e.preventDefault();
        settingsModal = true;
    }

    async function openAbout(e: Event) {
        e.preventDefault();
        aboutModal = true;
    }

</script>

{#if settingsModal}
    <SettingsModal bind:isOpen={settingsModal} {closeSidebar}/>
{/if}

{#if aboutModal}
    <AboutModal bind:isOpen={aboutModal} close={closeSidebar}/>
{/if}

<Drawer transitionType="fly" {transitionParams} bind:hidden={TempState.sideBarHidden}
        activateClickOutside={!settingsModal && !aboutModal}
        class="black:bg-gray-950 frappe:bg-ctp-base frappe:text-blue-10 overflow-hidden">
    <div class="flex items-center [&>button:has(span.sr-only):hover]:frappe:bg-ctp-pink [&>button:has(span.sr-only):hover]:frappe:text-ctp-base frappe:text-ctp-subtext0">
        <h5 id="drawer-navigation-label-3" class="text-base font-semibold text-gray-500 uppercase black:text-gray-400">
            Common Links</h5>
        <CloseButton on:click={closeSidebar} class="mb-4 black:text-white"/>
    </div>
    <Sidebar asideClass="h-[calc(100svh-4rem)]">
        <SidebarWrapper {divClass}>
            <SidebarGroup>
                <SidebarItem label="2b Protocols" href="https://2bprotocols.com/operational-and-clinical-guidelines/"
                             target="_blank"
                             rel="noopener noreferrer">
                    <svelte:fragment slot="icon">
                        <MedicalBook
                                class="w-5 h-5 text-gray-500 transition duration-75 black:text-gray-400 group-hover:text-gray-900 black:group-hover:text-white"/>
                    </svelte:fragment>
                </SidebarItem>

                <SidebarItem label="eHub" href="https://gardaworldus.teamehub.com" target="_blank"
                             rel="noopener noreferrer">
                    <svelte:fragment slot="icon">
                        <MoneyBillWave
                                class="w-5 h-5 text-gray-500 transition duration-75 black:text-gray-400 group-hover:text-gray-900 black:group-hover:text-white"/>
                    </svelte:fragment>
                </SidebarItem>

                <SidebarItem label="OfficerMetrics" href="https://guardmetrics.net/" target="_blank"
                             rel="noopener noreferrer">
                    <svelte:fragment slot="icon">
                        <Shield
                                class="w-5 h-5 text-gray-500 transition duration-75 black:text-gray-400 group-hover:text-gray-900
                        black:group-hover:text-white"/>
                    </svelte:fragment>
                </SidebarItem>

                <SidebarItem label="Academy" href="https://gardaworld-flw.myabsorb.ca/" target="_blank"
                             rel="noopener noreferrer">
                    <svelte:fragment slot="icon">
                        <GraduationCap
                                class="w-5 h-5 text-gray-500 transition duration-75 black:text-gray-400 group-hover:text-gray-900
                        black:group-hover:text-white"/>
                    </svelte:fragment>
                </SidebarItem>

                <SidebarItem label="Utah EMS" href="https://emslicense.utah.gov" target="_blank"
                             rel="noopener noreferrer">
                    <svelte:fragment slot="icon">
                        <Ambulance
                                class="w-5 h-5 text-gray-500 transition duration-75 black:text-gray-400 group-hover:text-gray-900
                        black:group-hover:text-white"/>
                    </svelte:fragment>
                </SidebarItem>

                <SidebarItem label="NREMT" href="https://www.nremt.org/" target="_blank"
                             rel="noopener noreferrer">
                    <svelte:fragment slot="icon">
                        <StarOfLife
                                class="w-5 h-5 text-gray-500 transition duration-75 black:text-gray-400 group-hover:text-gray-900
                        black:group-hover:text-white"/>
                    </svelte:fragment>
                </SidebarItem>
            </SidebarGroup>
            <SidebarGroup>

                <SidebarItem onclick={openFeedback} label="Feedback">
                    <svelte:fragment slot="icon">
                        <Feedback
                                class="w-5 h-5 text-gray-500 transition duration-75 black:text-gray-400 group-hover:text-gray-900 black:group-hover:text-white"/>
                    </svelte:fragment>
                </SidebarItem>

                <SidebarItem onclick={openSettings} label="Settings">
                    <svelte:fragment slot="icon">
                        <Cog
                                class="w-5 h-5 text-gray-500 transition duration-75 black:text-gray-400 group-hover:text-gray-900
                        black:group-hover:text-white"/>
                    </svelte:fragment>
                </SidebarItem>

                <SidebarItem onclick={openAbout} label="About">
                    <svelte:fragment slot="icon">
                        <Info
                                class="w-5 h-5 text-gray-500 transition duration-75 black:text-gray-400 group-hover:text-gray-900 black:group-hover:text-white"/>
                    </svelte:fragment>
                </SidebarItem>

                <SidebarItem href="https://work-app-git-200-carter121s-projects.vercel.app?_vercel_share=Wubh4UA5GNgPOiQynlHH3PTeBwkT3YlB" label="Beta">
                    <svelte:fragment slot="icon">
                        <Beta
                                class="w-5 h-5 text-gray-500 transition duration-75 black:text-gray-400 group-hover:text-gray-900 black:group-hover:text-white"/>
                    </svelte:fragment>
                </SidebarItem>

            </SidebarGroup>
        </SidebarWrapper>
    </Sidebar>
</Drawer>